import { useEffect, useState } from "react";
import { fetchWithAuth } from "@/core/data/fetch-with-auth";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import type { ReferenceResult } from "../../domain/entities/synthesis-session";

const ABSOLUTE_URL_RE = /^(?:https?:|data:|blob:)/i;

/** Canonical match key for a figure path: strip `<>`, percent-decode, collapse whitespace. */
export const normalizeImagePath = (src: string): string => {
  let s = (src ?? "").trim();
  if (s.startsWith("<") && s.endsWith(">")) s = s.slice(1, -1);
  try {
    s = decodeURIComponent(s.trim());
  } catch {
    // leave as-is if not valid percent-encoding
  }
  return s.replace(/\s+/g, " ").trim();
};

const unwrap = (src: string): string => {
  const s = src.trim();
  return s.startsWith("<") && s.endsWith(">") ? s.slice(1, -1).trim() : s;
};

/**
 * Pair each `![caption](path) [ID:x]` in the answer with its citation id, then
 * resolve `x -> documentId` via the message's references. Returns
 * `normalizedPath -> documentId` so a relative figure path can be fetched from
 * the backend. A figure always comes from a cited chunk, so its document is
 * known through that citation — no separate images channel is needed.
 */
export const buildImageDocMap = (
  content: string,
  refs: ReferenceResult[],
): Map<string, string> => {
  const byId = new Map(refs.map((r) => [r.id, r.documentId]));
  const map = new Map<string, string>();
  const re = /!\[[^\]]*\]\(\s*<?([^)>\s][^)>]*?)\s*>?\s*\)\s*\[ID:([^\]]+)\]/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(content ?? "")) !== null) {
    const documentId = byId.get(m[2]);
    if (documentId) map.set(normalizeImagePath(m[1]), documentId);
  }
  return map;
};

/** A resolved figure: a fixed 300x200 fit thumbnail that opens the full image. */
const FigureThumbnail = ({ src, alt }: { src: string; alt: string }) => (
  <Dialog>
    <DialogTrigger asChild>
      <button
        type="button"
        aria-label={`Xem ảnh: ${alt}`}
        className="my-2 block cursor-zoom-in overflow-hidden rounded-md border border-border bg-background/40 transition hover:border-[#2f8f36]/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2f8f36]/60"
      >
        <img
          src={src}
          alt={alt}
          className="h-[200px] w-[300px] object-contain"
        />
      </button>
    </DialogTrigger>
    <DialogContent
      showCloseButton
      className="max-h-[90vh] w-auto max-w-[95vw] overflow-auto p-2 sm:max-w-[95vw]"
    >
      <DialogTitle className="sr-only">{alt}</DialogTitle>
      {/* Natural size + scrollable content → large images scroll inside the dialog. */}
      <img src={src} alt={alt} className="block max-w-none" />
    </DialogContent>
  </Dialog>
);

type Props = {
  src?: string;
  caption?: string;
  imageDocs: Map<string, string>;
};

export const InlineFigureImage = ({ src, caption, imageDocs }: Props) => {
  const unwrapped = unwrap(src ?? "");
  const isAbsoluteUrl = ABSOLUTE_URL_RE.test(unwrapped);
  const key = normalizeImagePath(src ?? "");
  const documentId = isAbsoluteUrl ? undefined : imageDocs.get(key);

  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setObjectUrl(null);
    setFailed(false);
    if (!documentId) return;
    let url: string | null = null;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetchWithAuth(
          `/api/v1/documents/${documentId}/image?path=${encodeURIComponent(key)}`,
        );
        if (!res.ok) throw new Error(`status ${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setObjectUrl(url);
      } catch {
        if (!cancelled) setFailed(true);
      }
    };
    void load();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [documentId, key]);

  // The image source to display: a full URL directly, or the fetched blob.
  const displaySrc = isAbsoluteUrl
    ? unwrapped
    : documentId && !failed
      ? objectUrl
      : null;

  if (displaySrc) {
    return <FigureThumbnail src={displaySrc} alt={caption || "figure"} />;
  }

  // Unresolved (unknown citation, still streaming, or fetch failed) → caption
  // placeholder; never emit a raw <img src={relativePath}> (would 404 as a
  // relative SPA URL and flash a broken image).
  return (
    <span className="my-1 inline-flex items-center gap-1 rounded bg-background/60 px-2 py-1 text-xs text-muted-foreground">
      🖼 {caption || "image"}
    </span>
  );
};
